import { ArrowLeft, ClipboardList, Pencil } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/Common/button';
import questionnaireService from '../../services/questionnaireService';


interface QuestionOption {
  id: string;
  text: string;
  order: number;
  scale_level_identifier: string | null;
}

interface Question {
  id: string;
  text: string;
  order: number;
  question_type: string;
  options: QuestionOption[];
  config: any;
  scoring_criterion_id: string | null;
}

interface Questionnaire {
  id: string;
  name: string;
  description: string | null;
  owner_id: string;
  domain_id: string;
  questions: Question[];
}

const QuestionnaireDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadQuestionnaire = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const data = await questionnaireService.get(id);
        setQuestionnaire(data);
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || t('questionnaireDetail.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadQuestionnaire();
  }, [id]);

  const getQuestionTypeLabel = (type: string) => {
    return t(`questionnaireDetail.${type}`, type);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('questionnaireDetail.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !questionnaire) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/questionnaires')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('questionnaireDetail.back')}
        </Button>
        <div className="bg-destructive/10 border-l-4 border-destructive text-destructive p-4 rounded">
          {error || t('questionnaireDetail.notFound')}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/questionnaires')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('questionnaireDetail.back')}
          </Button>
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 p-2 rounded-md">
              <ClipboardList className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{questionnaire.name}</h1>
              {questionnaire.description && (
                <p className="text-sm text-muted-foreground mt-1">{questionnaire.description}</p>
              )}
            </div>
          </div>
        </div>
        <Button onClick={() => navigate(`/questionnaires/${id}/edit`)}>
          <Pencil className="h-4 w-4 mr-2" />
          {t('questionnaireDetail.edit')}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">{t('questionnaireDetail.questions')}</div>
          <div className="text-2xl font-bold text-foreground mt-1">
            {questionnaire.questions.length}
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">{t('questionnaireDetail.type')}</div>
          <div className="text-2xl font-bold text-foreground mt-1">
            {t('questionnaireDetail.model')}
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">{t('questionnaireDetail.domain')}</div>
          <div className="text-sm font-medium text-foreground mt-1 truncate">
            {questionnaire.domain_id}
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">{t('questionnaireDetail.questions')}</h2>
        </div>
        <div className="divide-y divide-border">
          {questionnaire.questions.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              {t('questionnaireDetail.noQuestions')}
            </div>
          ) : (
            questionnaire.questions
              .sort((a, b) => a.order - b.order)
              .map((question, index) => (
                <div key={question.id} className="p-6 hover:bg-accent/50 transition-colors">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-base font-medium text-foreground">{question.text}</h3>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-secondary text-secondary-foreground">
                              {getQuestionTypeLabel(question.question_type)}
                            </span>
                            {question.scoring_criterion_id && (
                              <span className="text-xs text-muted-foreground">
                                {t('questionnaireDetail.scoringCriterion')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Options */}
                      {question.options && question.options.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <div className="text-xs font-medium text-muted-foreground uppercase">
                            {t('questionnaireDetail.options')}
                          </div>
                          <div className="space-y-1">
                            {question.options
                              .sort((a, b) => a.order - b.order)
                              .map((option) => (
                                <div
                                  key={option.id}
                                  className="flex items-center gap-2 text-sm text-foreground bg-accent/50 rounded px-3 py-2"
                                >
                                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                                  <span>{option.text}</span>
                                  {option.scale_level_identifier && (
                                    <span className="text-xs text-muted-foreground ml-auto">
                                      ({option.scale_level_identifier})
                                    </span>
                                  )}
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireDetailPage;

